import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { Heart } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../lib/api";
import type { Product, ProductListResponse } from "../lib/types";
import { cn } from "../lib/utils";
import { useLikesStore } from "../stores/likes-store";

function updateProductLikeCaches(queryClient: QueryClient, productId: string, likeCount: number) {
  const updateProduct = (product: Product): Product =>
    product.id === productId ? { ...product, like_count: likeCount } : product;
  const updateList = (data: ProductListResponse | undefined): ProductListResponse | undefined =>
    data ? { ...data, items: data.items.map(updateProduct) } : data;

  queryClient.setQueriesData<ProductListResponse>({ queryKey: ["products"] }, updateList);
  queryClient.setQueriesData<ProductListResponse>({ queryKey: ["search"] }, updateList);
  queryClient.setQueriesData<Product>({ queryKey: ["product"] }, (data) =>
    data && data.id === productId ? updateProduct(data) : data
  );
}

type ProductLikeButtonProps = {
  product: Product;
  className?: string;
  size?: "sm" | "md";
};

export function ProductLikeButton({ product, className, size = "sm" }: ProductLikeButtonProps) {
  const queryClient = useQueryClient();
  const liked = useLikesStore((state) => Boolean(state.liked[product.id]));
  const setLiked = useLikesStore((state) => state.setLiked);
  const [likeCount, setLikeCount] = useState(product.like_count);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    setLikeCount(product.like_count);
  }, [product.like_count]);

  const toggleLike = async () => {
    if (isPending) return;
    const nextLiked = !liked;
    const previousCount = likeCount;
    const nextCount = Math.max(0, previousCount + (nextLiked ? 1 : -1));

    setIsPending(true);
    setLiked(product.id, nextLiked);
    setLikeCount(nextCount);
    updateProductLikeCaches(queryClient, product.id, nextCount);

    try {
      const response = await api.updateProductLike(product.id, nextLiked);
      setLikeCount(response.like_count);
      updateProductLikeCaches(queryClient, product.id, response.like_count);
    } catch {
      setLiked(product.id, liked);
      setLikeCount(previousCount);
      updateProductLikeCaches(queryClient, product.id, previousCount);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <button
      type="button"
      aria-label={liked ? "Unlike product" : "Like product"}
      aria-pressed={liked}
      onClick={toggleLike}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md border border-white/[0.08] bg-background/75 font-medium text-muted-foreground shadow-[0_8px_24px_rgba(0,0,0,0.2)] backdrop-blur transition duration-200 hover:border-accent/35 hover:text-foreground disabled:cursor-wait",
        size === "md" ? "h-10 px-3 text-sm" : "h-8 px-2.5 text-xs",
        liked && "border-red-500/35 text-red-400",
        className
      )}
      disabled={isPending}
    >
      <Heart
        className={cn(
          "transition duration-200",
          size === "md" ? "h-4 w-4" : "h-3.5 w-3.5",
          liked && "scale-110 fill-red-500 text-red-500"
        )}
      />
      <span>{likeCount}</span>
    </button>
  );
}
